import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useNavigate } from 'react-router-dom';
import {
    XMarkIcon,
    BookOpenIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import SearchableDropdown from '../common/SearchableDropdown';
import LessonTitleSearch from './LessonTitleSearch';
import { buildSowLessonOptions, filterSowLessonOptions } from '../../utils/sowLessons';

// Helper to check sub-strand match (handling arrays from SOW)
const isSubStrandMatch = (row, ss) => {
    if (!ss) return false;
    if (row.sub_strand_id && ss.id && String(row.sub_strand_id) === String(ss.id)) return true;
    if (!row.sub_strand || !ss.name) return false;

    const ssName = String(ss.name).toLowerCase();
    if (Array.isArray(row.sub_strand)) {
        return row.sub_strand.some(s => {
            if (!s) return false;
            const sLower = String(s).toLowerCase();
            return sLower === ssName || sLower.includes(ssName) || ssName.includes(sLower);
        });
    }

    const rSubStrand = String(row.sub_strand).toLowerCase();
    return rSubStrand === ssName || rSubStrand.includes(ssName) || ssName.includes(rSubStrand);
};

const LessonPlanModal = ({ subStrand = {}, courseId, onClose, editPlan = null, onEditSuccess = null }) => {
    const { get, post, patch } = useApi();
    const { showToast } = useAppState();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    
    // New SOW states
    const [learningAreas, setLearningAreas] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState(courseId || '');
    const [selectedStrandName, setSelectedStrandName] = useState('');
    const [selectedSubStrandItem, setSelectedSubStrandItem] = useState({});
    const [selectedStrandId, setSelectedStrandId] = useState('');
    const [curriculumStrands, setCurriculumStrands] = useState([]);
    const [curriculumSubStrands, setCurriculumSubStrands] = useState([]);
    
    // New SOW states
    const [sow, setSow] = useState(null);
    const [availableWeeks, setAvailableWeeks] = useState([]);
    const [availableLessons, setAvailableLessons] = useState([]);

    const [initialSowDataStr, setInitialSowDataStr] = useState('');

    // Memoised on the sub-strand's id and name: callers may pass a new object each render (or none at all),
    // and a changing identity would re-run every effect below after each keystroke
    const fixedSubStrandId = subStrand?.id;
    const fixedSubStrandName = subStrand?.name;
    const activeSubStrand = useMemo(
        () => (selectedSubStrandItem?.name ? selectedSubStrandItem : { id: fixedSubStrandId, name: fixedSubStrandName }),
        [selectedSubStrandItem, fixedSubStrandId, fixedSubStrandName]
    );

    // True once the teacher types their own title; the form then stops filling the title in by itself
    const [titleEdited, setTitleEdited] = useState(false);

    const [formData, setFormData] = useState({
        title: editPlan ? editPlan.title : (activeSubStrand?.name ? `Lesson Plan: ${activeSubStrand.name}` : ''),
        lesson_date: editPlan ? editPlan.lesson_date : new Date().toLocaleDateString('en-CA'),
        week_number: editPlan ? (editPlan.week_number || 1) : 1,
        template_id: '',
        sow_row_data: editPlan ? (editPlan.sow_row_data || null) : null
    });

    useEffect(() => {
        if (editPlan) {
            const ssName = editPlan.sub_strand_name || editPlan.sow_row_data?.sub_strand;
            const ssId = editPlan.sub_strand || editPlan.sub_strand_id || editPlan.sow_row_data?.sub_strand_id;
            if (ssName || ssId) {
                setSelectedSubStrandItem(prev => {
                    if (prev?.name === ssName && prev?.id === ssId) return prev;
                    return { id: ssId, name: ssName };
                });
            }
            if (editPlan.strand_name) {
                setSelectedStrandName(editPlan.strand_name);
            }
        }
    }, [editPlan]);

    useEffect(() => {
        if (editPlan && sow && sow.weekly_breakdown) {
            if (editPlan.sow_row_data && Object.keys(editPlan.sow_row_data).length > 0) {
                setInitialSowDataStr(JSON.stringify(editPlan.sow_row_data));
                setFormData(prev => ({ ...prev, sow_row_data: editPlan.sow_row_data }));
            } else {
                const match = sow.weekly_breakdown.find(row => 
                    (String(row.week) === String(editPlan.week_number) || String(row.week_number) === String(editPlan.week_number)) &&
                    isSubStrandMatch(row, activeSubStrand)
                );
                if (match) {
                    setInitialSowDataStr(JSON.stringify(match));
                    setFormData(prev => ({ ...prev, sow_row_data: match }));
                }
            }
        }
    }, [editPlan, sow, activeSubStrand]);

    useEffect(() => {
        let url = '/api/cbc/lesson-plans/?';
        const activeCourse = courseId || selectedCourseId;
        if (activeCourse) {
            url += `course=${activeCourse}`;
        } else {
            url += 'is_template=true';
        }
        
        get(url)
            .then(data => {
                let fetchedTemplates = data.results || data || [];
                setTemplates(fetchedTemplates);
            })
            .catch(err => console.error("Failed to fetch templates", err));
    }, [selectedCourseId, get, courseId]);

    useEffect(() => {
        if (!courseId) {
            get('/teachers/api/courses/')
                .then(res => setLearningAreas(res.courses || []))
                .catch(err => console.error(err));
        }
    }, [courseId, get]);

    // Fetch SOW
    useEffect(() => {
        const fetchSowForCourse = selectedCourseId || courseId;
        if (fetchSowForCourse) {
            get(`/api/cbc/schemes-of-work/teacher-sow/?learning_area=${fetchSowForCourse}`)
                .then(res => {
                    const sowData = res.data;
                    setSow(sowData);
                })
                .catch(err => console.error("Failed to fetch SOW", err));
        }
    }, [courseId, selectedCourseId, get]);

    // Fetch Curriculum Strands
    useEffect(() => {
        const fetchStrandsForCourse = selectedCourseId || courseId;
        if (fetchStrandsForCourse) {
            get(`/api/cbc/learning-areas/${fetchStrandsForCourse}/strands/`)
                .then(data => setCurriculumStrands(Array.isArray(data) ? data : []))
                .catch(err => console.error("Failed to load curriculum strands", err));
        } else {
            setCurriculumStrands([]);
        }
    }, [courseId, selectedCourseId, get]);

    // Fetch Curriculum Sub-strands
    useEffect(() => {
        if (selectedStrandId) {
            get(`/api/cbc/sub-strands/?strand=${selectedStrandId}`)
                .then(data => setCurriculumSubStrands(Array.isArray(data) ? data : []))
                .catch(err => console.error("Failed to load curriculum sub-strands", err));
        } else {
            setCurriculumSubStrands([]);
        }
    }, [selectedStrandId, get]);

    // Sync selectedStrandId from selectedStrandName if populated via template
    useEffect(() => {
        if (selectedStrandName && curriculumStrands.length > 0) {
            const matchedStrand = curriculumStrands.find(s => s.name === selectedStrandName);
            if (matchedStrand && matchedStrand.id !== selectedStrandId) {
                setSelectedStrandId(matchedStrand.id);
            }
        }
    }, [selectedStrandName, selectedStrandId, curriculumStrands]);

    // Filter available weeks when SOW or subStrand changes
    useEffect(() => {
        if (sow && sow.weekly_breakdown) {
            const weeksSet = new Set();
            sow.weekly_breakdown.forEach(row => {
                if (isSubStrandMatch(row, activeSubStrand)) {
                    weeksSet.add(row.week || row.week_number);
                }
            });
            const weeks = Array.from(weeksSet).sort((a,b) => a - b);
            setAvailableWeeks(weeks);
            
            setFormData(prev => {
                if (weeks.length > 0 && !weeks.includes(Number(prev.week_number)) && !weeks.includes(String(prev.week_number))) {
                    return { ...prev, week_number: weeks[0] };
                }
                return prev;
            });
        }
    }, [sow, activeSubStrand]);

    // Filter available lessons when week changes
    useEffect(() => {
        if (sow && sow.weekly_breakdown && formData.week_number) {
            const lessons = sow.weekly_breakdown.filter(row => 
                (String(row.week) === String(formData.week_number) || String(row.week_number) === String(formData.week_number)) &&
                isSubStrandMatch(row, activeSubStrand)
            );
            setAvailableLessons(lessons);
            const currentSelectedWeek = formData.sow_row_data ? (formData.sow_row_data.week || formData.sow_row_data.week_number) : null;
            if (String(currentSelectedWeek) !== String(formData.week_number)) {
                if (lessons.length > 0) {
                    setFormData(prev => ({ ...prev, sow_row_data: lessons[0] }));
                } else {
                    setFormData(prev => ({ ...prev, sow_row_data: null }));
                }
            }
        }
    }, [sow, formData.week_number, activeSubStrand, formData.sow_row_data]);

    // The title the form fills in by itself: the chosen SoW lesson's title, else "Lesson Plan: <sub-strand>", else nothing
    const autoTitle = formData.sow_row_data?.lesson_title
        || formData.sow_row_data?.title
        || (activeSubStrand?.name ? `Lesson Plan: ${activeSubStrand.name}` : '');

    useEffect(() => {
        if (!editPlan && !formData.template_id && !titleEdited) {
            setFormData(prev => (prev.title === autoTitle ? prev : { ...prev, title: autoTitle }));
        }
    }, [autoTitle, editPlan, formData.template_id, titleEdited]);

    // Every lesson of the SoW with the number it has on the Scheme of Work screen
    const lessonOptions = useMemo(() => buildSowLessonOptions(sow?.weekly_breakdown), [sow]);

    const lessonLabelFor = (row, fallbackIndex) =>
        lessonOptions.find(option => option.row === row)?.lessonLabel || `Lesson ${fallbackIndex + 1}`;

    // Lessons offered by the title search: narrowed to the sub-strand when one is fixed or chosen
    const titleSearchOptions = useMemo(() => {
        const hasSubStrand = Boolean(activeSubStrand?.name || activeSubStrand?.id);
        return filterSowLessonOptions(lessonOptions, {
            matchesSubStrand: hasSubStrand ? (row) => isSubStrandMatch(row, activeSubStrand) : null
        });
    }, [lessonOptions, activeSubStrand]);

    // Finds the curriculum strand and sub-strand a SoW row belongs to (the row only carries their names)
    const resolveSubStrandForRow = async (row) => {
        const rowStrands = (Array.isArray(row.strand) ? row.strand : [row.strand])
            .map(name => String(name || '').trim().toLowerCase())
            .filter(Boolean);
        const candidates = rowStrands.length > 0
            ? curriculumStrands.filter(s => rowStrands.includes(String(s.name).trim().toLowerCase()))
            : curriculumStrands;
        for (const strand of candidates) {
            try {
                const list = await get(`/api/cbc/sub-strands/?strand=${strand.id}`);
                const match = (Array.isArray(list) ? list : []).find(ss => isSubStrandMatch(row, ss));
                if (match) {
                    setSelectedStrandId(strand.id);
                    setSelectedStrandName(strand.name);
                    setSelectedSubStrandItem({ id: match.id, name: match.name });
                    return true;
                }
            } catch (err) {
                console.error('Failed to resolve sub-strand for lesson', err);
            }
        }
        return false;
    };

    // Teacher picked a lesson from the title search: fill title, week, lesson and (when not fixed) strand / sub-strand
    const handleSelectSowLesson = async (option) => {
        setTitleEdited(false);
        setFormData(prev => ({
            ...prev,
            title: option.title || prev.title,
            week_number: option.week,
            sow_row_data: option.row
        }));
        const subStrandIsFixed = Boolean(subStrand?.name);
        if (!subStrandIsFixed && !isSubStrandMatch(option.row, activeSubStrand)) {
            const resolved = await resolveSubStrandForRow(option.row);
            if (!resolved) {
                showToast('Pick the sub-strand for this lesson', 'warning');
            }
        }
    };

    useEffect(() => {
        if (!editPlan && formData.template_id) {
            const selectedTemplate = templates.find(t => t.id === parseInt(formData.template_id) || t.id === formData.template_id);
            if (selectedTemplate) {
                // Unconditionally update learning area if not fixed by props
                let matchedLAId = selectedCourseId;
                if (!courseId) {
                    let matchedLA = learningAreas.find(la => la.id === selectedTemplate.learning_area_id);
                    if (!matchedLA) {
                        matchedLA = learningAreas.find(la => la.name === selectedTemplate.learning_area_name && la.grade_level_name === selectedTemplate.grade_level_name);
                    }
                    if (matchedLA) {
                        matchedLAId = matchedLA.id;
                    } else if (selectedTemplate.learning_area_id) {
                        matchedLAId = selectedTemplate.learning_area_id;
                    }
                }
                
                if (matchedLAId !== selectedCourseId) {
                    setSelectedCourseId(matchedLAId);
                }

                // Unconditionally update strand and sub-strand
                setSelectedStrandName(selectedTemplate.strand_name || '');
                if (selectedTemplate.sub_strand_name) {
                    setSelectedSubStrandItem({ id: selectedTemplate.sub_strand, name: selectedTemplate.sub_strand_name });
                } else {
                    setSelectedSubStrandItem({});
                }

                setFormData(prev => ({
                    ...prev,
                    title: selectedTemplate.title || `Lesson Plan: ${activeSubStrand?.name || ''}`,
                    lesson_date: selectedTemplate.lesson_date || prev.lesson_date,
                    week_number: selectedTemplate.week_number || prev.week_number || 1,
                    sow_row_data: selectedTemplate.sow_row_data || null
                }));
            }
        }
    }, [formData.template_id, templates, editPlan, courseId, selectedCourseId, selectedStrandName, selectedSubStrandItem, activeSubStrand?.name, learningAreas]);

    const isRegenerating = editPlan && (initialSowDataStr !== JSON.stringify(formData.sow_row_data) || formData.week_number !== editPlan.week_number);

    const handleGenerateDraft = async () => {
        if (!subStrand?.id && !activeSubStrand?.id) return;

        if (editPlan && !isRegenerating) {
            try {
                setSaving(true);
                const response = await patch(`/api/cbc/lesson-plans/${editPlan.id}/`, {
                    title: formData.title,
                    lesson_date: formData.lesson_date,
                    week_number: formData.week_number,
                    sow_row_data: formData.sow_row_data
                });
                if (response || response.id) {
                    showToast('Lesson plan updated successfully', 'success');
                    if (onEditSuccess) onEditSuccess();
                    onClose();
                }
            } catch (err) {
                showToast('Failed to update lesson plan', 'error');
            } finally {
                setSaving(false);
            }
            return;
        }

        try {
            setSaving(true);
            const payload = {
                sub_strand_id: activeSubStrand.id,
                lesson_date: formData.lesson_date,
                week_number: formData.week_number,
                template_id: formData.template_id,
                title: formData.title,
                sow_row_data: formData.sow_row_data
            };
            
            if (editPlan && isRegenerating) {
                payload.regenerate_id = editPlan.id;
            }

            const response = await post('/api/cbc/lesson-plans/generate_from_substrand/', payload);

            if (response.success && response.lesson_plan) {
                showToast(editPlan ? 'Lesson plan regenerated successfully' : 'Lesson plan generated successfully', 'success');
                if (editPlan && onEditSuccess) {
                    onEditSuccess();
                    onClose();
                } else {
                    onClose();
                    navigate(`/teacher/lesson-plan/editor/${response.lesson_plan.id}`);
                }
            } else {
                showToast(response.message || 'Failed to generate plan', 'error');
            }
        } catch (error) {
            console.error('Error generating lesson plan:', error);
            showToast('Failed to generate lesson plan', 'error');
        } finally {
            setSaving(false);
        }
    };

    const noLearningAreaYet = !courseId && !selectedCourseId;

    // Plain box when editing an existing plan; otherwise it also searches the Scheme of Work lessons by title
    const lessonTitleField = (
        <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">Lesson Title</label>
            {editPlan ? (
                <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-[#18216D]"
                    placeholder="Enter title for new plan..."
                    autoFocus
                />
            ) : (
                <LessonTitleSearch
                    value={formData.title}
                    onChange={(text) => {
                        setTitleEdited(true);
                        setFormData(prev => ({ ...prev, title: text }));
                    }}
                    autoValue={autoTitle}
                    options={titleSearchOptions}
                    onSelect={handleSelectSowLesson}
                    placeholder="Search the scheme of work by title, or type your own..."
                    disabledMessage={noLearningAreaYet ? 'Select a learning area to search its lessons' : ''}
                    emptyMessage={activeSubStrand?.name
                        ? 'No scheme of work lessons found for this sub-strand. Type a custom title.'
                        : 'No scheme of work lessons found. Type a custom title.'}
                />
            )}
        </div>
    );

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto custom-scrollbar flex flex-col border border-slate-100 animate-in zoom-in-95 duration-200">
                <div className="px-8 py-6 bg-white border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-2xl text-blue-600">
                            <BookOpenIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-[#18216D]">{editPlan ? 'Edit Lesson Plan' : 'Create New Plan'}</h3>
                            <p className="text-sm text-slate-500 font-medium truncate max-w-[200px]" title={subStrand?.name}>
                                {subStrand?.name}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-400 hover:text-red-500"
                    >
                        <XMarkIcon className="w-6 h-6 stroke-[2.5]" />
                    </button>
                </div>

                <div className="p-8 space-y-4">
                    {!editPlan && (
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Use Template (Optional)</label>
                            <SearchableDropdown
                                options={templates.map(t => ({ id: t.id, name: t.title, badge: t.is_template ? null : 'Draft' }))}
                                badgeKey="badge"
                                selectedValues={formData.template_id ? [formData.template_id] : []}
                                onChange={(val) => setFormData({ ...formData, template_id: val })}
                                placeholder="-- None --"
                            />
                        </div>
                    )}
                    
                    {!editPlan && !subStrand?.name && (
                        <div className="grid grid-cols-1 gap-4">
                            {!courseId && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Learning Area</label>
                                    <SearchableDropdown
                                        options={learningAreas.map(la => ({ id: la.id, name: `${la.name} - ${la.grade_level_name} ${la.stream_display_name && la.stream_display_name !== 'All Streams' ? `(${la.stream_display_name})` : ''}`.trim() }))}
                                        selectedValues={selectedCourseId ? [selectedCourseId] : []}
                                        onChange={(val) => {
                                            if (val !== selectedCourseId) {
                                                setSelectedCourseId(val);
                                                setSelectedStrandId('');
                                                setSelectedStrandName('');
                                                setSelectedSubStrandItem({});
                                                // The old learning area's scheme of work no longer applies
                                                setSow(null);
                                                setAvailableWeeks([]);
                                                setAvailableLessons([]);
                                                setTitleEdited(false);
                                                setFormData(prev => ({ ...prev, title: '', week_number: 1, sow_row_data: null }));
                                            }
                                        }}
                                        placeholder="Select Learning Area..."
                                    />
                                </div>
                            )}
                            {lessonTitleField}
                            {selectedCourseId && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Strand</label>
                                        <SearchableDropdown
                                            options={curriculumStrands.map(s => ({ id: s.id, name: s.name }))}
                                            selectedValues={selectedStrandId ? [selectedStrandId] : []}
                                            onChange={(val) => {
                                                setSelectedStrandId(val);
                                                const strand = curriculumStrands.find(s => s.id === val);
                                                setSelectedStrandName(strand ? strand.name : '');
                                                setSelectedSubStrandItem({});
                                            }}
                                            placeholder="Select Strand..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Sub-strand</label>
                                        <SearchableDropdown
                                            options={curriculumSubStrands.map(ss => ({ id: ss.id, name: ss.name }))}
                                            selectedValues={selectedSubStrandItem?.id ? [selectedSubStrandItem.id] : []}
                                            onChange={(val) => {
                                                const ss = curriculumSubStrands.find(s => s.id === val);
                                                if (ss) {
                                                    setSelectedSubStrandItem({ name: ss.name, id: ss.id });
                                                }
                                            }}
                                            placeholder="Select Sub-strand..."
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* With the strand / sub-strand dropdowns showing, the title sits above them (see above) */}
                    {(editPlan || subStrand?.name) && lessonTitleField}
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Target Date</label>
                        <input
                            type="date"
                            value={formData.lesson_date}
                            onChange={(e) => setFormData({ ...formData, lesson_date: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-[#18216D]"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Target Week</label>
                            {availableWeeks.length > 0 ? (
                                <select
                                    value={formData.week_number}
                                    onChange={(e) => setFormData({ ...formData, week_number: parseInt(e.target.value) })}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-[#18216D]"
                                >
                                    {availableWeeks.map(w => (
                                        <option key={w} value={w}>Week {w}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="number"
                                    min="1"
                                    max="14"
                                    value={formData.week_number}
                                    onChange={(e) => setFormData({ ...formData, week_number: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-[#18216D]"
                                />
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Target Lesson</label>
                            {availableLessons.length > 0 ? (
                                <select
                                    value={formData.sow_row_data ? availableLessons.findIndex(row => {
                                        if (row === formData.sow_row_data) return true;
                                        if (!row || !formData.sow_row_data) return false;
                                        if (row.$$id && formData.sow_row_data.$$id && row.$$id === formData.sow_row_data.$$id) return true;
                                        
                                        const lessonA = String(row.lessons || row.lesson || '').trim();
                                        const lessonB = String(formData.sow_row_data.lessons || formData.sow_row_data.lesson || '').trim();
                                        if (lessonA && lessonB && lessonA === lessonB) return true;
                                        
                                        const titleA = (row.title || '').trim();
                                        const titleB = (formData.sow_row_data.title || '').trim();
                                        if (titleA && titleB && titleA === titleB) return true;
                                        
                                        const cleanA = { ...row }; delete cleanA.$$id;
                                        const cleanB = { ...formData.sow_row_data }; delete cleanB.$$id;
                                        return JSON.stringify(cleanA) === JSON.stringify(cleanB);
                                    }) : -1}
                                    onChange={(e) => {
                                        const idx = parseInt(e.target.value);
                                        if (!isNaN(idx) && availableLessons[idx]) {
                                            setTitleEdited(false); // choosing a lesson takes its title
                                            setFormData({ ...formData, sow_row_data: availableLessons[idx] });
                                        } else {
                                            setFormData({ ...formData, sow_row_data: null });
                                        }
                                    }}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium text-[#18216D]"
                                >
                                    <option value="-1">-- Select Lesson --</option>
                                    {availableLessons.map((l, i) => (
                                        <option key={i} value={i}>
                                            {lessonLabelFor(l, i)}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 font-medium text-sm">
                                    No mapped lessons
                                </div>
                            )}
                        </div>
                    </div>
                    {isRegenerating && (
                        <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800 font-medium">
                                Changing the target week or lesson will <strong>regenerate</strong> the lesson plan's content to match the new topic, overwriting any custom edits you made.
                            </p>
                        </div>
                    )}
                </div>

                <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex items-center gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 text-sm font-bold text-slate-600 hover:bg-slate-200 bg-slate-100 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleGenerateDraft}
                        disabled={saving}
                        className="flex-1 px-4 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {saving ? (
                            <span className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                {editPlan ? (isRegenerating ? 'Regenerating...' : 'Saving...') : (formData.template_id ? 'Opening...' : 'Generating...')}
                            </span>
                        ) : (
                            <>
                                {editPlan ? (isRegenerating ? 'Regenerate & Save' : 'Save Changes') : (formData.template_id ? 'Open plan' : 'Generate Lesson Plan')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LessonPlanModal;
